import { useCallback, useEffect, useRef, useState } from 'react';
import { quizPhase } from '@/lib/quizClock';
import type { FanQuiz } from '@/lib/quiz';

// Mirrors usePollAutoOpen: the sheet counts as seen only once the fan closes
// it, so the reload the PWA does on its first visit after a deploy cannot
// hide the pop-up. Per quiz, per browser session.
const shownKey = (id: string) => `quiz_popup_shown_${id}`;
const resultsKey = (id: string) => `quiz_results_shown_${id}`;
// Survives the session: a reload between the last question and the final
// must still bring the winner back to the claim code.
const playedKey = (id: string) => `quiz_played_${id}`;

function sessionHas(key: string): boolean {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}
function sessionSet(key: string) {
  try { sessionStorage.setItem(key, '1'); } catch { /* private mode: the sheet may pop up again */ }
}

/** The fan answered at least once: the sheet comes back by itself at the end (the winner must see the code). */
export function markQuizPlayed(quizId: string) {
  try { localStorage.setItem(playedKey(quizId), '1'); } catch { /* ignore */ }
}
function hasPlayed(quizId: string): boolean {
  try { return localStorage.getItem(playedKey(quizId)) === '1'; } catch { return false; }
}

const isOver = (q: FanQuiz, nowMs: number) =>
  q.status === 'finished' || (q.status === 'running' && quizPhase(q, nowMs).phase === 'finished');

/**
 * Which quiz sheet is open, and the pop-up: a running quiz opens once per
 * session; a quiz that just ended opens once more for fans who played. Never
 * over a poll sheet (the poll closes first, then the quiz pops up).
 */
export function useQuizAutoOpen({ quizzes, pollSheetOpen }: { quizzes: readonly FanQuiz[]; pollSheetOpen: boolean }) {
  const [openQuizId, setOpenQuizId] = useState<string | null>(null);
  const quizzesRef = useRef(quizzes);
  quizzesRef.current = quizzes;

  useEffect(() => {
    if (openQuizId || pollSheetOpen) return;
    const now = Date.now();
    for (const q of quizzes) {
      if (q.status !== 'running' && q.status !== 'finished') continue;
      if (isOver(q, now)) {
        if (hasPlayed(q.id) && !sessionHas(resultsKey(q.id))) { setOpenQuizId(q.id); return; }
      } else if (!sessionHas(shownKey(q.id))) {
        setOpenQuizId(q.id);
        return;
      }
    }
  }, [quizzes, pollSheetOpen, openQuizId]);

  const openQuiz = useCallback((id: string) => setOpenQuizId(id), []);
  const closeQuiz = useCallback(() => {
    const id = openRef.current;
    if (id) {
      sessionSet(shownKey(id));
      const q = quizzesRef.current.find((x) => x.id === id);
      if (q && isOver(q, Date.now())) sessionSet(resultsKey(id));
    }
    setOpenQuizId(null);
  }, []);
  const openRef = useRef(openQuizId);
  openRef.current = openQuizId;

  return { openQuizId, openQuiz, closeQuiz };
}
